import { useState, useEffect } from 'react';
import styled from 'styled-components';
import colors from '../../../styles/colors';
import { useAppSelector } from '../../../store/hooks';
import { apiGetActivePacks, apiGetMyCardAccount, apiOpenPack } from '../../../api/userCardApi';
import type { ActivePack, PackOpenResult } from '../../../api/userCardApi';
import { apiGetCard } from '../../../api/cardApi';
import { apiGetExpansion } from '../../../api/expansionApi';
import type { ExpansionCardItem } from '../../../types/models/expansion';
import PackRevealModal from '../../Cards/PackRevealModal';
import CardDetailModal from '../../Cards/CardDetailModal';
import type { Card } from '../../../types/models/card';

const storageUrl = process.env.REACT_APP_STORAGE_URL ?? '';

const toImgSrc = (path: string) =>
  path.startsWith('http') ? path : storageUrl + path;

const Container = styled.div`
  padding: 20px 16px 70px;
  min-height: calc(100vh - 70px);
`;

const Title = styled.h1`
  font-size: 20px;
  font-weight: 700;
  color: gold;
  margin: 0 0 4px;
`;

const Subtitle = styled.p`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.4);
  margin: 0 0 16px;
`;

const CreditsBar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: ${colors.bgColorBlack2};
  border: 1px solid rgba(255, 215, 0, 0.3);
  border-radius: 8px;
  padding: 8px 14px;
  margin-bottom: 20px;
`;

const CreditsLabel = styled.span`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const CreditsValue = styled.span`
  font-size: 15px;
  font-weight: 700;
  color: gold;
`;

const PackGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
`;

const PackCard = styled.div`
  background-color: ${colors.bgColorBlack2};
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 10px;
  overflow: hidden;
  cursor: pointer;
  active { opacity: 0.85; }
`;

const PackImgWrapper = styled.div`
  width: 100%;
  aspect-ratio: 2 / 3;
  background-color: #2a2a2a;
  overflow: hidden;
`;

const PackImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const PackImgPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.15);
  font-size: 12px;
`;

const PackBody = styled.div`
  padding: 8px 10px 4px;
`;

const PackName = styled.h3`
  font-size: 12px;
  font-weight: 700;
  color: #fff;
  margin: 0 0 2px;
`;

const PackMeta = styled.p`
  font-size: 10px;
  color: rgba(255, 255, 255, 0.45);
  margin: 0 0 1px;
`;

const PackNotes = styled.p`
  font-size: 10px;
  color: rgba(255, 255, 255, 0.55);
  font-style: italic;
  margin: 3px 0 0;
  line-height: 1.4;
`;

const PackHint = styled.p`
  font-size: 10px;
  color: rgba(255, 215, 0, 0.5);
  margin: 4px 0 0;
  font-style: italic;
`;

const PackCost = styled.p`
  font-size: 12px;
  font-weight: 700;
  color: gold;
  margin: 4px 0 0;
`;

const BuyBtn = styled.button<{ $disabled?: boolean }>`
  width: calc(100% - 16px);
  margin: 8px 8px 10px;
  padding: 8px 4px;
  background-color: ${({ $disabled }) => ($disabled ? 'rgba(255,215,0,0.05)' : 'rgba(255,215,0,0.15)')};
  border: 1px solid ${({ $disabled }) => ($disabled ? 'rgba(255,215,0,0.2)' : 'gold')};
  border-radius: 6px;
  color: ${({ $disabled }) => ($disabled ? 'rgba(255,215,0,0.35)' : 'gold')};
  font-size: 11px;
  font-weight: 600;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
`;

const ErrorMsg = styled.p`
  font-size: 10px;
  color: #e05555;
  margin: 0 8px 6px;
  text-align: center;
`;

const StatusText = styled.p`
  text-align: center;
  color: rgba(255, 255, 255, 0.4);
  margin-top: 32px;
  font-size: 14px;
`;

// ─── Preview Bottom Sheet ─────────────────────────────────────────────────────

const PreviewOverlay = styled.div`
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.7);
  z-index: 900;
  display: flex;
  align-items: flex-end;
`;

const PreviewSheet = styled.div`
  width: 100%;
  max-height: 80vh;
  background-color: ${colors.bgColorBlack2};
  border-top: 1px solid rgba(255, 215, 0, 0.2);
  border-radius: 14px 14px 0 0;
  display: flex;
  flex-direction: column;
`;

const PreviewHandle = styled.div`
  width: 36px;
  height: 4px;
  background-color: rgba(255, 255, 255, 0.15);
  border-radius: 2px;
  margin: 10px auto 0;
  flex-shrink: 0;
`;

const PreviewHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px 10px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.07);
  flex-shrink: 0;
`;

const PreviewTitle = styled.h2`
  font-size: 15px;
  font-weight: 700;
  color: #fff;
  margin: 0 0 2px;
`;

const PreviewSubtitle = styled.p`
  font-size: 11px;
  color: rgba(255, 255, 255, 0.4);
  margin: 0;
`;

const PreviewCloseBtn = styled.button`
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.45);
  font-size: 20px;
  cursor: pointer;
  padding: 0 4px;
  line-height: 1;
`;

const PreviewBody = styled.div`
  overflow-y: auto;
  padding: 14px 12px 24px;
  flex: 1;
`;

const PreviewEmpty = styled.p`
  text-align: center;
  color: rgba(255, 255, 255, 0.35);
  font-size: 13px;
  margin-top: 24px;
`;

const PreviewGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
`;

const PreviewCardWrapper = styled.div`
  aspect-ratio: 2 / 3;
  background-color: #2a2a2a;
  border-radius: 5px;
  overflow: hidden;
  cursor: pointer;
  border: 1px solid rgba(255, 255, 255, 0.06);
`;

const PreviewCardImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const PreviewCardPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.15);
  font-size: 10px;
`;

// ─── Component ────────────────────────────────────────────────────────────────

const PackShopMobile: React.FC = () => {
  const isAuthenticated = useAppSelector(
    (state) => state.auth.isAuthenticated || state.auth.userLoggedState !== null
  );
  const [packs, setPacks] = useState<ActivePack[]>([]);
  const [credits, setCredits] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [openingId, setOpeningId] = useState<number | null>(null);
  const [packErrors, setPackErrors] = useState<Record<number, string>>({});
  const [revealResult, setRevealResult] = useState<PackOpenResult | null>(null);

  // Preview espansione
  const [previewPack, setPreviewPack] = useState<ActivePack | null>(null);
  const [previewCards, setPreviewCards] = useState<ExpansionCardItem[] | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [expansionCache, setExpansionCache] = useState<Record<number, ExpansionCardItem[]>>({});

  // Dettaglio carta da preview
  const [detailCard, setDetailCard] = useState<Card | null>(null);
  const [detailLoading, setDetailLoading] = useState<number | null>(null);
  const [detailCache, setDetailCache] = useState<Record<number, Card>>({});

  useEffect(() => {
    apiGetActivePacks()
      .then((data) => setPacks(Array.isArray(data) ? data : (data as any).data ?? []))
      .catch(() => setPacks([]))
      .finally(() => setIsLoading(false));
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;
    apiGetMyCardAccount()
      .then((data) => setCredits(data.credits))
      .catch(() => {});
  }, [isAuthenticated]);

  const handlePackClick = async (pack: ActivePack) => {
    setPreviewPack(pack);
    const expId = pack.expansion?.id;
    if (!expId) { setPreviewCards([]); return; }
    if (expansionCache[expId]) { setPreviewCards(expansionCache[expId]); return; }
    setPreviewLoading(true);
    setPreviewCards(null);
    try {
      const exp = await apiGetExpansion(expId);
      const cards = exp.cards ?? [];
      setExpansionCache((prev) => ({ ...prev, [expId]: cards }));
      setPreviewCards(cards);
    } catch {
      setPreviewCards([]);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleBuy = async (e: React.MouseEvent, pack: ActivePack) => {
    e.stopPropagation();
    setOpeningId(pack.id);
    setPackErrors((prev) => ({ ...prev, [pack.id]: '' }));
    try {
      const result = await apiOpenPack(pack.id);
      if (!Array.isArray(result.cards)) throw new Error('invalid_response');
      setCredits(result.credits_remaining);
      setRevealResult(result);
    } catch (err: any) {
      const msg = err?.message?.includes('insufficient_credits')
        ? 'Crediti insufficienti'
        : 'Errore durante l\'acquisto';
      setPackErrors((prev) => ({ ...prev, [pack.id]: msg }));
    } finally {
      setOpeningId(null);
    }
  };

  return (
    <>
      <Container>
        <Title>Acquista Pacchetti</Title>
        <Subtitle>Tocca un pacchetto per vedere le carte disponibili.</Subtitle>

        {isAuthenticated && (
          <CreditsBar>
            <CreditsLabel>Crediti:</CreditsLabel>
            <CreditsValue>{credits !== null ? credits : '—'}</CreditsValue>
          </CreditsBar>
        )}

        {isLoading && <StatusText>Caricamento...</StatusText>}
        {!isLoading && packs.length === 0 && (
          <StatusText>Nessun pacchetto disponibile.</StatusText>
        )}

        <PackGrid>
          {packs.map((pack) => {
            const src = pack.expansion?.expansion_path
              ? toImgSrc(pack.expansion.expansion_path)
              : null;
            return (
              <PackCard key={pack.id} onClick={() => handlePackClick(pack)}>
                <PackImgWrapper>
                  {src
                    ? <PackImg src={src} alt={pack.expansion.name} />
                    : <PackImgPlaceholder>Nessuna immagine</PackImgPlaceholder>
                  }
                </PackImgWrapper>
                <PackBody>
                  <PackName>{pack.name}</PackName>
                  <PackMeta>{pack.cards_per_pack} carte per pack</PackMeta>
                  <PackMeta>{pack.expansion.name}</PackMeta>
                  {pack.expansion.notes && <PackNotes>{pack.expansion.notes}</PackNotes>}
                  <PackCost>{pack.cost} cr.</PackCost>
                  <PackHint>Tocca per vedere le carte</PackHint>
                </PackBody>
                {packErrors[pack.id] && <ErrorMsg>{packErrors[pack.id]}</ErrorMsg>}
                <BuyBtn
                  $disabled={!isAuthenticated || openingId === pack.id}
                  disabled={!isAuthenticated || openingId === pack.id}
                  onClick={(e) => isAuthenticated && handleBuy(e, pack)}
                >
                  {!isAuthenticated
                    ? 'Accedi'
                    : openingId === pack.id
                      ? 'Apertura...'
                      : `Acquista`}
                </BuyBtn>
              </PackCard>
            );
          })}
        </PackGrid>
      </Container>

      {/* Bottom sheet anteprima carte */}
      {previewPack && (
        <PreviewOverlay onClick={(e) => e.target === e.currentTarget && setPreviewPack(null)}>
          <PreviewSheet>
            <PreviewHandle />
            <PreviewHeader>
              <div>
                <PreviewTitle>Carte disponibili</PreviewTitle>
                <PreviewSubtitle>
                  {previewPack.expansion?.name} · {previewCards?.length ?? '…'} carte nel pool
                </PreviewSubtitle>
              </div>
              <PreviewCloseBtn onClick={() => setPreviewPack(null)}>✕</PreviewCloseBtn>
            </PreviewHeader>
            <PreviewBody>
              {previewLoading && <PreviewEmpty>Caricamento...</PreviewEmpty>}
              {!previewLoading && previewCards?.length === 0 && (
                <PreviewEmpty>Nessuna carta trovata per questa espansione.</PreviewEmpty>
              )}
              {!previewLoading && previewCards && previewCards.length > 0 && (
                <PreviewGrid>
                  {previewCards.map((card) => {
                    const imgSrc = card.fronte_path ? toImgSrc(card.fronte_path) : null;
                    return (
                      <PreviewCardWrapper key={card.id} onClick={async () => {
                          if (detailLoading === card.id) return;
                          if (detailCache[card.id]) { setDetailCard(detailCache[card.id]); return; }
                          setDetailLoading(card.id);
                          try {
                            const full = await apiGetCard(card.id);
                            setDetailCache((prev) => ({ ...prev, [card.id]: full }));
                            setDetailCard(full);
                          } catch { } finally { setDetailLoading(null); }
                        }}
                        style={{ cursor: detailLoading === card.id ? 'wait' : 'pointer' }}
                      >
                        {imgSrc
                          ? <PreviewCardImg src={imgSrc} alt={`Carta #${card.id}`} />
                          : <PreviewCardPlaceholder>#{card.id}</PreviewCardPlaceholder>
                        }
                      </PreviewCardWrapper>
                    );
                  })}
                </PreviewGrid>
              )}
            </PreviewBody>
          </PreviewSheet>
        </PreviewOverlay>
      )}

      {/* Dettaglio carta da preview */}
      {detailCard && (
        <CardDetailModal
          card={detailCard}
          ownedQuantity={0}
          isAuthenticated={isAuthenticated}
          onClose={() => setDetailCard(null)}
        />
      )}

      {revealResult && (
        <PackRevealModal
          cards={revealResult.cards}
          alreadyOwned={revealResult.already_owned}
          creditsRemaining={revealResult.credits_remaining}
          onClose={() => setRevealResult(null)}
        />
      )}
    </>
  );
};

export default PackShopMobile;
